import { fromInteger } from '@repo/money'
import { isAuthenticatedPlayer } from 'src/types/domain/Player'
import { usePlayerStore } from 'src/store/player'
import { useBetStore } from 'src/store/bet'
import { useGameStore } from 'src/store/game'
import { useSocketStore } from 'src/store/socket'
import { useSoundStore } from 'src/store/sound'
import { useUIStore } from 'src/store/ui'
import { promisifyPush } from 'src/utils/promisifyPush'
import { PostMessageBridge } from './PostMessageBridge'
import {
  InboundCommandType,
  MuteAudioPayloadSchema,
  OutboundEventType,
  SetAudioVolumePayloadSchema,
  type StateSnapshotPayload
} from './types'

/**
 * Register all inbound command handlers on the bridge.
 */
export function registerInboundHandlers(): void {
  const bridge = PostMessageBridge.getInstance()

  bridge.registerHandler(InboundCommandType.REFRESH_BALANCE, handleRefreshBalance)
  bridge.registerHandler(InboundCommandType.MUTE_AUDIO, handleMuteAudio)
  bridge.registerHandler(InboundCommandType.SET_AUDIO_VOLUME, handleSetAudioVolume)
  bridge.registerHandler(InboundCommandType.OPEN_GAME_HISTORY, handleOpenGameHistory)
  bridge.registerHandler(InboundCommandType.CLOSE_PANELS, handleClosePanels)
  bridge.registerHandler(InboundCommandType.GET_STATE, handleGetState)
}

// ── Handlers ──

async function handleRefreshBalance(): Promise<void> {
  const playerChannel = useSocketStore.getState().playerChannel
  if (!playerChannel || playerChannel.state !== 'joined') return

  const player = usePlayerStore.getState().currentPlayer
  if (!isAuthenticatedPlayer(player)) return

  try {
    const result = await promisifyPush({
      channel: playerChannel,
      eventName: 'client:player:balance:get'
    })

    const responseDTO = result as { amount: number; currency: string }
    const balance = {
      amount: fromInteger(responseDTO.amount, responseDTO.currency),
      currency: responseDTO.currency
    }

    usePlayerStore.getState().actions.balanceChanged(balance)
  } catch {
    // Balance fetch failed — existing balance stays
  }
}

function handleMuteAudio(payload: Record<string, unknown>): void {
  const result = MuteAudioPayloadSchema.safeParse(payload)
  if (!result.success) return

  const actions = useSoundStore.getState().actions
  if (result.data.muted) {
    actions.muteMaster()
  } else {
    actions.unmuteMaster()
  }
}

function handleSetAudioVolume(payload: Record<string, unknown>): void {
  const result = SetAudioVolumePayloadSchema.safeParse(payload)
  if (!result.success) return

  useSoundStore.getState().actions.changeMasterVolume(result.data.volume)
}

function handleOpenGameHistory(): void {
  const actions = useUIStore.getState().actions
  actions.sidebarOpened(true)
  actions.tabActivated('betHistory')
}

function handleClosePanels(): void {
  const actions = useUIStore.getState().actions
  actions.sidebarOpened(false)
  actions.tracksPanelOpened(false)
  actions.audioSettingsOpened(false)
}

function handleGetState(_payload: Record<string, unknown>, requestId?: string): void {
  const player = usePlayerStore.getState().currentPlayer
  const gameState = useGameStore.getState()
  const betState = useBetStore.getState()
  const soundState = useSoundStore.getState()

  const isAuthenticated = isAuthenticatedPlayer(player)
  const hasActiveBet = betState.bets.allIds.some((id) => {
    const bet = betState.bets.byId[id]
    return bet?.status === 'pending'
  })

  const snapshot: StateSnapshotPayload = {
    isLoaded: isAuthenticated,
    playerId: isAuthenticated ? player.id : undefined,
    gameId: gameState.currentGameId ?? undefined,
    balance: isAuthenticated ? player.balance : undefined,
    isMuted: soundState.muted.master,
    hasActiveBet
  }

  PostMessageBridge.getInstance().send(OutboundEventType.STATE_SNAPSHOT, snapshot, requestId)
}
